import type React from "react";
import {memo, useCallback, useEffect, useState} from "react";

import {usePrevious} from "../../react";
import {HStack} from "../ui/hstack";

import SelectAMPM from "./SelectAMPM";
import SelectHour from "./SelectHour";
import SelectMinute from "./SelectMinute";
import {convertMinuteToHour, convertMinuteToMinute, convertMinuteToUnit} from "./util";

type Props = {
    minutes: number; // minutes;
    showMinute?: boolean;
    onChange: (minutes: number) => void;
};

const SelectTime: React.FC<Props> = ({minutes, showMinute, onChange}) => {
    const [hour, setHour] = useState(convertMinuteToHour(minutes));
    const [unit, setUnit] = useState(convertMinuteToUnit(minutes));
    const [minute, setMinute] = useState(convertMinuteToMinute(minutes));

    const prevValue = usePrevious(minutes);

    useEffect(() => {
        if (hour === undefined || unit === undefined || minute === undefined) {
            return;
        }
        // compute the value based on hour and unit
        // Convert to 12-hour format
        let hours = hour % 12;
        if (hours === 0) hours = 12;

        // Apply AM/PM adjustment
        const adjustedHours =
            unit === 12 ? (hours === 12 ? 12 : hours + 12) : hours === 12 ? 0 : hours;

        const nextValue = adjustedHours * 60 + minute;
        // don't call this in the effect which makes form dirty immediately
        if (prevValue !== nextValue) {
            onChange(nextValue);
        }
    }, [hour, minute, unit, prevValue, onChange]);

    const handleHourChange = useCallback((value: number) => {
        setHour(value);
    }, []);

    const handleUnitChange = useCallback((value: number) => {
        setUnit(value);
    }, []);

    const handleMinuteChange = useCallback((value: number) => {
        setMinute(value);
    }, []);

    return (
        <HStack space="sm">
            <SelectHour
                className="flex-shrink flex-grow"
                minutes={typeof hour === "number" ? hour * 60 : undefined}
                onChange={handleHourChange}
            />
            {showMinute && (
                <SelectMinute
                    className="flex-shrink flex-grow"
                    minutes={minute}
                    onChange={handleMinuteChange}
                />
            )}
            <SelectAMPM
                className="flex-shrink flex-grow"
                minutes={typeof unit === "number" ? unit * 60 : undefined}
                onChange={handleUnitChange}
            />
        </HStack>
    );
};

export default memo(SelectTime);
